import { throttle } from 'lodash';
import { useEffect, useMemo } from 'react';

const useDocumentEventThrottled = <TEventType extends keyof WindowEventMap>(
  eventType: TEventType,
  onEvent: (event: WindowEventMap[TEventType]) => void,
  timeoutThrottle = 300,
  options?: AddEventListenerOptions,
) => {
  const handleEventThrottle = useMemo(
    () => throttle(onEvent, timeoutThrottle),
    [onEvent, timeoutThrottle],
  );

  useEffect(() => {
    window.addEventListener(eventType, handleEventThrottle, options);

    return () => {
      window.removeEventListener(eventType, handleEventThrottle, options);
      handleEventThrottle.cancel();
    };
  }, [eventType, handleEventThrottle]);
};

export const useDocumentScrollThrottled = (
  onScroll: (event: Event) => void,
  timeoutThrottle?: number,
) => {
  return useDocumentEventThrottled('scroll', onScroll, timeoutThrottle);
};

export const useDocumentTouchMoveThrottled = (
  onTouchMove: (event: TouchEvent) => void,
  timeoutThrottle?: number,
) => {
  return useDocumentEventThrottled('touchmove', onTouchMove, timeoutThrottle, {
    passive: false,
  });
};
